// Página StockMovements - Gestión de movimientos de stock
// Permite registrar entradas, salidas y ajustes de stock

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getAllWarehouses } from '../../services/warehouses.service'
import { getCollection } from '../../services/firestore.service'
import { applyStockMovement, getStockMovements } from '../../services/stock.service'
import { useAuth } from '../../hooks/useAuth'
import { useBusinessSettingsContext } from '../../hooks/useBusinessSettingsContext'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import './StockMovements.css'

const buildVariantLabel = (variant, index) => {
  const selection = variant?.selection || {}
  const color = selection.colorLabel || selection.color || selection.colorHex || selection.colorValue || ''
  const size = selection.size || selection.talle || selection.talla || ''
  const label = [color, size].filter(Boolean).join(' | ')
  if (label) return label
  if (variant?.sku) return `SKU ${variant.sku}`
  return `Variante ${index + 1}`
}

const getVariantSelection = (variant) => {
  const selection = variant?.selection || {}
  return {
    color: selection.color || selection.colorHex || selection.colorValue || selection.hex || null,
    size: selection.size || selection.talle || selection.talla || null
  }
}

/**
 * Página StockMovements
 * Gestión de movimientos de stock (entrada, salida, ajuste)
 */
function StockMovements() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const [searchParams] = useSearchParams()
  const productParam = searchParams.get('product')
  const warehouseParam = searchParams.get('warehouse')
  const { activeProfile } = useBusinessSettingsContext()

  const [warehouses, setWarehouses] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [notification, setNotification] = useState(null)

  // Formulario
  const [formData, setFormData] = useState({
    productId: productParam || '',
    warehouseId: warehouseParam || '',
    type: 'entrada', // 'entrada', 'salida', 'ajuste'
    quantity: '',
    reason: '',
    variantKey: '',
    size: '',
    color: ''
  })

  // Historial de movimientos
  const [movements, setMovements] = useState([])
  const [showHistory, setShowHistory] = useState(false)
  const [historyFilters, setHistoryFilters] = useState({
    productId: '',
    warehouseId: '',
    type: ''
  })

  // Cargar datos iniciales
  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true)
      const [warehousesData, productsData] = await Promise.all([
        getAllWarehouses(),
        getCollection('products')
      ])
      setWarehouses(warehousesData)
      setProducts(productsData)
    } catch (err) {
      console.error('Error cargando datos:', err)
      setNotification({
        type: 'error',
        message: 'Error al cargar los datos'
      })
      setTimeout(() => setNotification(null), 3000)
    } finally {
      setLoading(false)
    }
  }, [])

  const selectedProduct = useMemo(() => {
    return products.find(p => p.id === formData.productId) || null
  }, [products, formData.productId])

  const productSizes = useMemo(() => {
    const list = Array.isArray(selectedProduct?.sizes) ? selectedProduct.sizes : []
    return list.filter(s => typeof s === 'string' && s.trim())
  }, [selectedProduct])

  const productColors = useMemo(() => {
    const list = Array.isArray(selectedProduct?.colors) ? selectedProduct.colors : []
    return list.filter(c => typeof c === 'string' && c.startsWith('#'))
  }, [selectedProduct])

  const colorLabels = useMemo(() => {
    const map = selectedProduct?.colorNames && typeof selectedProduct.colorNames === 'object' ? selectedProduct.colorNames : {}
    return map
  }, [selectedProduct])

  const variantOptions = useMemo(() => {
    const variants = Array.isArray(selectedProduct?.variants) ? selectedProduct.variants : []
    return variants
      .map((variant, index) => {
        const selection = getVariantSelection(variant)
        if (!selection.color && !selection.size) return null
        return {
          key: `v-${index}`,
          label: buildVariantLabel(variant, index),
          variant
        }
      })
      .filter(Boolean)
  }, [selectedProduct])

  const selectedVariant = useMemo(() => {
    if (!formData.variantKey) return null
    const option = variantOptions.find(item => item.key === formData.variantKey)
    return option?.variant || null
  }, [formData.variantKey, variantOptions])

  const requiresSize = useMemo(() => {
    if (activeProfile?.rubro === 'indumentaria') return productSizes.length > 0
    return productSizes.length > 0
  }, [activeProfile?.rubro, productSizes.length])

  const requiresColor = useMemo(() => {
    if (activeProfile?.rubro === 'indumentaria') return productColors.length > 0
    return productColors.length > 0
  }, [activeProfile?.rubro, productColors.length])

  useEffect(() => {
    if (variantOptions.length > 0 && !formData.variantKey) {
      setFormData(prev => ({ ...prev, variantKey: variantOptions[0]?.key || '' }))
    }
    if (productSizes.length === 1 && !formData.size) {
      setFormData(prev => ({ ...prev, size: productSizes[0] }))
    }
    if (productColors.length === 1 && !formData.color) {
      setFormData(prev => ({ ...prev, color: productColors[0] }))
    }
  }, [formData.color, formData.size, formData.variantKey, productColors, productSizes, variantOptions])

  useEffect(() => {
    if (variantOptions.length === 0) {
      if (formData.variantKey) {
        setFormData(prev => ({ ...prev, variantKey: '' }))
      }
      return
    }
    if (!selectedVariant) return
    const selection = getVariantSelection(selectedVariant)
    setFormData(prev => ({
      ...prev,
      color: selection.color ?? prev.color,
      size: selection.size ?? prev.size
    }))
  }, [formData.variantKey, selectedVariant, variantOptions])

  const fetchHistory = useCallback(async () => {
    try {
      const filters = {}
      if (historyFilters.productId) filters.productId = historyFilters.productId
      if (historyFilters.warehouseId) filters.warehouseId = historyFilters.warehouseId
      if (historyFilters.type) filters.type = historyFilters.type

      const movementsData = await getStockMovements(filters)
      
      // Enriquecer con datos de productos y depósitos
      const enriched = movementsData.map(movement => {
        const product = products.find(p => p.id === movement.productId)
        const warehouse = warehouses.find(w => w.id === movement.warehouseId)
        return {
          ...movement,
          productName: product?.name || 'Producto no encontrado',
          warehouseName: warehouse?.name || 'Depósito no encontrado',
          size: movement.size || null,
          color: movement.color || null
        }
      })

      setMovements(enriched)
    } catch (err) {
      console.error('Error cargando historial:', err)
    }
  }, [historyFilters, products, warehouses])

  useEffect(() => {
    fetchInitialData()
  }, [fetchInitialData])

  useEffect(() => {
    if (showHistory) {
      fetchHistory()
    }
  }, [showHistory, fetchHistory])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (saving) return

    // Validaciones
    if (!formData.productId) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar un producto'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (!formData.warehouseId) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar un depósito'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const quantity = parseFloat(formData.quantity)
    if (isNaN(quantity) || quantity <= 0) {
      setNotification({
        type: 'error',
        message: 'La cantidad debe ser un número mayor a 0'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (formData.type === 'ajuste' && quantity < 0) {
      setNotification({
        type: 'error',
        message: 'El ajuste no puede ser negativo'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (variantOptions.length > 0 && !formData.variantKey) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar una variante'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const variantSelection = selectedVariant ? getVariantSelection(selectedVariant) : null
    const movementColor = requiresColor ? (variantSelection?.color || formData.color) : null
    const movementSize = requiresSize ? (variantSelection?.size || formData.size) : null

    if (requiresColor && !movementColor) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar un color'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (requiresSize && !movementSize) {
      setNotification({
        type: 'error',
        message: 'Debes seleccionar un talle'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    setSaving(true)

    try {
      await applyStockMovement({
        productId: formData.productId,
        warehouseId: formData.warehouseId,
        type: formData.type,
        quantity: quantity,
        reason: formData.reason || '',
        userId: currentUser?.uid || null,
        size: requiresSize ? movementSize : null,
        color: requiresColor ? movementColor : null
      })

      setNotification({
        type: 'success',
        message: `Movimiento de ${formData.type} registrado correctamente`
      })

      // Limpiar formulario
      setFormData(prev => ({
        ...prev,
        quantity: '',
        reason: '',
        size: requiresSize ? prev.size : '',
        color: requiresColor ? prev.color : ''
      }))

      // Recargar historial si está visible
      if (showHistory) {
        fetchHistory()
      }

      // Redirigir a la página de stock del depósito
      setTimeout(() => {
        navigate(`/admin/stock?warehouse=${formData.warehouseId}`)
      }, 1500)
    } catch (error) {
      console.error('Error registrando movimiento:', error)
      setNotification({
        type: 'error',
        message: `Error al registrar movimiento: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      setSaving(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleHistoryFilterChange = (e) => {
    const { name, value } = e.target
    setHistoryFilters(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const getMovementTypeLabel = (type) => {
    const labels = {
      entrada: 'Entrada',
      salida: 'Salida',
      ajuste: 'Ajuste',
      transferencia: 'Transferencia'
    }
    return labels[type] || type
  }

  const getMovementTypeIcon = (type) => {
    const icons = {
      entrada: '➕',
      salida: '➖',
      ajuste: '🔧',
      transferencia: '🔄'
    }
    return icons[type] || '📝'
  }

  if (loading) {
    return (
      <div className="stock-movements-page">
        <div className="loading-state">
          <p>Cargando datos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="stock-movements-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      {/* Header */}
      <div className="movements-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/stock')}
          >
            ← Volver
          </button>
          <h1>Movimientos de Stock</h1>
          <p className="movements-subtitle">
            Registra entradas, salidas y ajustes de stock
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
        >
          {showHistory ? '📋 Ocultar Historial' : '📋 Ver Historial'}
        </Button>
      </div>

      <div className="movements-content">
        {/* Formulario de movimiento */}
        <div className="movement-form-section">
          <h2 className="section-title">Registrar Movimiento</h2>
          <form onSubmit={handleSubmit} className="movement-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="productId">
                  Producto <span className="required">*</span>
                </label>
                <select
                  id="productId"
                  name="productId"
                  value={formData.productId}
                  onChange={handleChange}
                  required
                  disabled={saving}
                  className="form-input"
                >
                  <option value="">Seleccionar producto...</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.sku})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="warehouseId">
                  Depósito <span className="required">*</span>
                </label>
                <select
                  id="warehouseId"
                  name="warehouseId"
                  value={formData.warehouseId}
                  onChange={handleChange}
                  required
                  disabled={saving}
                  className="form-input"
                >
                  <option value="">Seleccionar depósito...</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="type">
                  Tipo de Movimiento <span className="required">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled={saving}
                  className="form-input"
                >
                  <option value="entrada">➕ Entrada</option>
                  <option value="salida">➖ Salida</option>
                  <option value="ajuste">🔧 Ajuste</option>
                </select>
                <small className="form-hint">
                  {formData.type === 'entrada' && 'Agrega stock al depósito'}
                  {formData.type === 'salida' && 'Reduce stock del depósito'}
                  {formData.type === 'ajuste' && 'Establece la cantidad exacta de stock'}
                </small>
              </div>

            {variantOptions.length > 0 ? (
              <div className="form-group">
                <label htmlFor="variantKey">
                  Variante <span className="required">*</span>
                </label>
                <select
                  id="variantKey"
                  name="variantKey"
                  value={formData.variantKey}
                  onChange={handleChange}
                  required
                  disabled={saving}
                  className="form-input"
                >
                  <option value="">Seleccionar variante...</option>
                  {variantOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {requiresColor ? (
              <div className="form-group">
                <label htmlFor="color">
                  Color <span className="required">*</span>
                </label>
                <select
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  required={variantOptions.length === 0}
                  disabled={saving || variantOptions.length > 0}
                  className="form-input"
                >
                  <option value="">Seleccionar color...</option>
                  {productColors.map((c) => (
                    <option key={c} value={c}>
                      {colorLabels?.[c] || c}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {requiresSize ? (
              <div className="form-group">
                <label htmlFor="size">
                  Talle <span className="required">*</span>
                </label>
                <select
                  id="size"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  required={variantOptions.length === 0}
                  disabled={saving || variantOptions.length > 0}
                  className="form-input"
                >
                  <option value="">Seleccionar talle...</option>
                  {productSizes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

              <div className="form-group">
                <label htmlFor="quantity">
                  Cantidad <span className="required">*</span>
                </label>
                <input
                  type="number"
                  id="quantity"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  placeholder="0"
                  min="0"
                  step="1"
                  required
                  disabled={saving}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reason">Motivo / Observaciones</label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                placeholder="Ej: Recepción de pedido, Ajuste de inventario, Venta..."
                rows={3}
                disabled={saving}
                className="form-input"
              />
            </div>

            <div className="form-actions">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/stock')}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
              >
                {saving ? 'Registrando...' : 'Registrar Movimiento'}
              </Button>
            </div>
          </form>
        </div>

        {/* Historial de movimientos */}
        {showHistory && (
          <div className="movements-history-section">
            <h2 className="section-title">Historial de Movimientos</h2>
            
            {/* Filtros del historial */}
            <div className="history-filters">
              <div className="form-group">
                <label htmlFor="historyProduct">Producto:</label>
                <select
                  id="historyProduct"
                  name="productId"
                  value={historyFilters.productId}
                  onChange={handleHistoryFilterChange}
                  className="form-input"
                >
                  <option value="">Todos los productos</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} ({product.sku})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="historyWarehouse">Depósito:</label>
                <select
                  id="historyWarehouse"
                  name="warehouseId"
                  value={historyFilters.warehouseId}
                  onChange={handleHistoryFilterChange}
                  className="form-input"
                >
                  <option value="">Todos los depósitos</option>
                  {warehouses.map(warehouse => (
                    <option key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="historyType">Tipo:</label>
                <select
                  id="historyType"
                  name="type"
                  value={historyFilters.type}
                  onChange={handleHistoryFilterChange}
                  className="form-input"
                >
                  <option value="">Todos los tipos</option>
                  <option value="entrada">Entrada</option>
                  <option value="salida">Salida</option>
                  <option value="ajuste">Ajuste</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
            </div>

            {/* Tabla de historial */}
            <div className="history-table-container">
              {movements.length === 0 ? (
                <div className="empty-state">
                  <p>No se encontraron movimientos con los filtros aplicados</p>
                </div>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Producto</th>
                      <th>Depósito</th>
                      <th>Talle</th>
                      <th>Color</th>
                      <th>Tipo</th>
                      <th>Cantidad</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((movement) => (
                      <tr key={movement.id}>
                        <td>
                          {movement.createdAt 
                            ? new Date(movement.createdAt.seconds * 1000).toLocaleString('es-AR')
                            : 'N/A'}
                        </td>
                        <td>{movement.productName}</td>
                        <td>{movement.warehouseName}</td>
                        <td>{movement.size || '-'}</td>
                        <td>{movement.color || '-'}</td>
                        <td>
                          <span className={`movement-type-badge ${movement.type}`}>
                            {getMovementTypeIcon(movement.type)} {getMovementTypeLabel(movement.type)}
                          </span>
                        </td>
                        <td>
                          <span className="quantity-value">{movement.quantity}</span>
                        </td>
                        <td className="reason-cell">{movement.reason || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StockMovements

